import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Page } from "../_shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Trash2, Search, ShieldCheck, Edit, Download } from "lucide-react";
import { toast } from "sonner";

const PLAN_BADGE = {
  starter: "bg-secondary text-muted-foreground border-border",
  growth: "bg-primary/15 text-primary border-primary/30",
  pro: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
};

export default function AdminUsers() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [planFilter, setPlanFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [skip, setSkip] = useState(0);
  const limit = 25;

  const load = () => {
    const params = { skip, limit };
    if (q) params.q = q;
    if (planFilter !== "all") params.plan = planFilter;
    api.get("/admin/users", { params }).then((r) => setData(r.data));
  };
  useEffect(() => { load(); }, [skip, planFilter]); // eslint-disable-line

  const remove = async (uid, email) => {
    if (!window.confirm(`Excluir usuário ${email} e todos os seus dados?`)) return;
    try {
      await api.delete(`/admin/users/${uid}`);
      toast.success("Usuário removido");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/admin/users/${editing.id}`, {
        plan: editing.plan, is_active: editing.is_active, name: editing.name, company: editing.company,
      });
      toast.success("Usuário atualizado");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    }
  };

  const exportCsv = async (kind) => {
    const path = kind === "leads" ? "/admin/exports/leads.csv" : "/admin/exports/users.csv";
    try {
      const r = await api.get(path, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const cd = r.headers?.["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      a.href = url;
      a.download = m ? m[1] : `smartstockai-${kind}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exportação de ${kind === "leads" ? "leads" : "usuários"} pronta`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao exportar");
    }
  };

  return (
    <Page title="Usuários" subtitle={data ? `${data.total} usuários cadastrados` : "Carregando..."}>
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-64">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="admin-users-search" placeholder="Buscar email, nome, empresa..." value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (setSkip(0), load())} className="pl-9" />
        </div>
        <Select value={planFilter} onValueChange={(v) => { setPlanFilter(v); setSkip(0); }}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os planos</SelectItem>
            <SelectItem value="starter">Starter</SelectItem>
            <SelectItem value="growth">Growth</SelectItem>
            <SelectItem value="pro">Pro</SelectItem>
          </SelectContent>
        </Select>
        <Button data-testid="admin-users-search-btn" onClick={() => { setSkip(0); load(); }} className="bg-primary hover:bg-primary/90">Buscar</Button>
        <Button
          data-testid="admin-export-users-csv"
          onClick={() => exportCsv("users")}
          variant="outline"
          className="border-primary/40 text-primary hover:bg-primary/10"
        >
          <Download className="w-4 h-4 mr-2" /> Exportar usuários
        </Button>
        <Button
          data-testid="admin-export-leads-csv"
          onClick={() => exportCsv("leads")}
          variant="outline"
          className="border-border"
        >
          <Download className="w-4 h-4 mr-2" /> Exportar leads
        </Button>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden mt-4">
        {!data ? (
          <div className="p-6 space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead>Usuário</TableHead>
                <TableHead>Empresa</TableHead>
                <TableHead>Plano</TableHead>
                <TableHead className="text-right">Produtos</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Cadastrado</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((u) => (
                <TableRow key={u.id} data-testid={`admin-user-${u.id}`} className="border-border">
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div>
                        <div className="font-medium flex items-center gap-2">
                          {u.name}
                          {u.is_admin && <ShieldCheck className="w-3.5 h-3.5 text-red-400" />}
                        </div>
                        <div className="text-xs text-muted-foreground">{u.email}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{u.company || "—"}</TableCell>
                  <TableCell>
                    <span className={`text-xs px-2 py-0.5 rounded-md border ${PLAN_BADGE[u.plan] || PLAN_BADGE.free}`}>
                      {u.plan}
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-mono">{u.products}</TableCell>
                  <TableCell>
                    <span className={`text-xs ${u.is_active ? "text-emerald-400" : "text-red-400"}`}>
                      {u.is_active ? "ativo" : "desativado"}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{(u.created_at || "").slice(0, 10)}</TableCell>
                  <TableCell>
                    <div className="flex gap-1 justify-end">
                      <Button data-testid={`admin-edit-${u.id}`} variant="ghost" size="icon" onClick={() => setEditing({ ...u })}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      {!u.is_admin && (
                        <Button data-testid={`admin-delete-${u.id}`} variant="ghost" size="icon" onClick={() => remove(u.id, u.email)}>
                          <Trash2 className="w-4 h-4 text-muted-foreground hover:text-red-400" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {data && data.total > limit && (
        <div className="flex items-center justify-between mt-4 text-sm">
          <Button variant="outline" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))}>Anterior</Button>
          <span className="text-muted-foreground">{skip + 1}–{Math.min(skip + limit, data.total)} de {data.total}</span>
          <Button variant="outline" disabled={skip + limit >= data.total} onClick={() => setSkip(skip + limit)}>Próximo</Button>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Editar usuário</DialogTitle></DialogHeader>
          {editing && (
            <form onSubmit={save} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground">Nome</label>
                <Input className="mt-2" value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground">Empresa</label>
                <Input className="mt-2" value={editing.company || ""} onChange={(e) => setEditing({ ...editing, company: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground">Plano</label>
                <Select value={editing.plan} onValueChange={(v) => setEditing({ ...editing, plan: v })}>
                  <SelectTrigger data-testid="edit-plan-select" className="mt-2"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="starter">Starter (R$ 149/mês)</SelectItem>
                    <SelectItem value="growth">Growth (R$ 397/mês)</SelectItem>
                    <SelectItem value="pro">Pro (R$ 1.497/mês)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg border border-border bg-secondary/40">
                <div>
                  <div className="text-sm font-medium">Conta ativa</div>
                  <div className="text-xs text-muted-foreground">Desative para bloquear acesso</div>
                </div>
                <Switch data-testid="edit-active-switch" checked={editing.is_active !== false} onCheckedChange={(v) => setEditing({ ...editing, is_active: v })} />
              </div>
              <DialogFooter>
                <Button data-testid="edit-save-btn" type="submit" className="bg-primary hover:bg-primary/90 w-full">Salvar</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </Page>
  );
}
